import {
  Field,
  Text,
  GetStaticComponentProps,
  useSitecoreContext,
  useComponentProps,
  constants,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useI18n } from 'next-localization';
import { ComponentProps } from 'lib/component-props';
import { getSiteRecruitingStudiesByNctIds } from '../../GraphQL/queries/siteRecruitingStudies.query';
import { getBucketFolderIdForSite } from '../../lib/utils/bucketFolderId';
import { StSiteMasterSection, StSiteMasterSectionTitle } from '../SiteMaster/SiteMaster.styles';
import {
  RelatedProtocolSiteItem,
  SiteMasterRouteFields,
  SiteRecruitingStudiesComponentData,
  SiteRecruitingStudy,
} from '../SiteMaster/types';
import { getFieldValue } from '../SiteMaster/utils';
import SiteRecruitingStudyCard from './SiteRecruitingStudyCard';

type SiteRecruitingStudiesProps = ComponentProps & {
  fields: {
    sectionTitle?: Field<string>;
    viewStudyDetailsText?: Field<string>;
    eligibilityButtonText?: Field<string>;
  };
};

type ProtocolNctRow = {
  nctId: string;
  conditionLabels: string[];
};

const FALLBACK_VIEW_STUDY_DETAILS = 'View Details';
const FALLBACK_ELIGIBILITY = 'Check eligibility';
const FALLBACK_ENROLLING = 'Enrolling';
const FALLBACK_TITLE_LINE_COUNT = 1;
const FALLBACK_DESCRIPTION_LINE_COUNT = 3;

const toLineCount = (raw: unknown, fallback: number): number => {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

/** Prioritize studies that can render the eligibility CTA (stable sort keeps relative order). */
export const sortStudiesByEligibility = (studies: SiteRecruitingStudy[]): SiteRecruitingStudy[] =>
  [...studies].sort(
    (a, b) => Number(Boolean(b.eligibilityUrl)) - Number(Boolean(a.eligibilityUrl))
  );

/** Sync parse of layout multilist → NCT list + indication descriptions (chip labels). */
export const mapRelatedProtocols = (items?: RelatedProtocolSiteItem[] | null): ProtocolNctRow[] => {
  if (!items?.length) {
    return [];
  }

  return items
    .map((item) => {
      const nctId = getFieldValue(item.fields?.nctId);
      if (!nctId) {
        return null;
      }

      const conditionLabels = (item.fields?.indication ?? [])
        .map((indication) => getFieldValue(indication.fields?.description))
        .filter(Boolean);

      return { nctId, conditionLabels };
    })
    .filter((row): row is ProtocolNctRow => row !== null);
};

/**
 * Server-side load used by this component's getStaticProps only.
 * Uses .env STUDY_FOLDER_ID + Sitecore API key — do not call from the browser.
 */
export const loadSiteRecruitingStudies = async (
  relatedProtocolSites: RelatedProtocolSiteItem[] | null | undefined,
  locale: string,
  siteName: string
): Promise<SiteRecruitingStudy[]> => {
  const protocolRows = mapRelatedProtocols(relatedProtocolSites);
  const nctIds = protocolRows.map((row) => row.nctId);

  if (!nctIds.length) {
    return [];
  }

  const studyFolderId = getBucketFolderIdForSite(process.env.STUDY_FOLDER_ID, siteName);
  if (!studyFolderId) {
    return [];
  }

  const gqlStudies = await getSiteRecruitingStudiesByNctIds(nctIds, locale, studyFolderId);
  const conditionByNct = new Map(protocolRows.map((row) => [row.nctId, row.conditionLabels]));

  return sortStudiesByEligibility(
    gqlStudies.map((study) => ({
      ...study,
      conditionLabels: conditionByNct.get(study.nctId) || [],
    }))
  );
};

/**
 * Owner of recruiting-studies GraphQL load.
 * Hero / Explore read this component's getStaticProps via useSiteRecruitingStudiesProps.
 */
const SiteRecruitingStudies = (props: SiteRecruitingStudiesProps): JSX.Element | null => {
  const { fields, rendering } = props;
  const { t } = useI18n();

  // Already sorted eligibility-first by loadSiteRecruitingStudies at build/request time.
  const componentData = useComponentProps<SiteRecruitingStudiesComponentData>(rendering.uid);
  const studies = componentData?.studies ?? [];

  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  if (studies.length === 0) {
    return null;
  }

  const titleLineCount = toLineCount(
    isMobile
      ? getFieldValue(siteSettings?.studyTitleLineCountForMobile)
      : isTablet
        ? getFieldValue(siteSettings?.studyTitleLineCountForTablet)
        : getFieldValue(siteSettings?.studyTitleLineCountForDesktop),
    FALLBACK_TITLE_LINE_COUNT
  );
  const descriptionLineCount = toLineCount(
    isMobile
      ? getFieldValue(siteSettings?.studyDescriptionLineCountForMobile)
      : isTablet
        ? getFieldValue(siteSettings?.studyDescriptionLineCountForTablet)
        : getFieldValue(siteSettings?.studyDescriptionLineCountForDesktop),
    FALLBACK_DESCRIPTION_LINE_COUNT
  );

  const viewStudyDetailsLabel = getFieldValue(fields.viewStudyDetailsText) ? (
    <Text field={fields.viewStudyDetailsText} />
  ) : (
    FALLBACK_VIEW_STUDY_DETAILS
  );

  const eligibilityLabel = getFieldValue(fields.eligibilityButtonText) ? (
    <Text field={fields.eligibilityButtonText} />
  ) : (
    FALLBACK_ELIGIBILITY
  );

  const enrollingStatusLabel = t('enrolling') || FALLBACK_ENROLLING;
  const studyCount = studies.length;
  const sectionTitle = getFieldValue(fields.sectionTitle);

  return (
    <StSiteMasterSection
      id="site-recruiting-studies"
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <StSiteMasterSectionTitle
        sx={{ marginBottom: 0, fontSize: (theme) => theme.typography.pxToRem(16) }}
      >
        {sectionTitle ? <Text field={fields.sectionTitle} /> : null}
        {` (${studyCount})`}
      </StSiteMasterSectionTitle>

      {studies.map((study) => (
        <SiteRecruitingStudyCard
          key={study.nctId}
          study={study}
          viewStudyDetailsLabel={viewStudyDetailsLabel}
          eligibilityLabel={eligibilityLabel}
          enrollingStatusLabel={enrollingStatusLabel}
          titleLineCount={titleLineCount}
          descriptionLineCount={descriptionLineCount}
        />
      ))}
    </StSiteMasterSection>
  );
};

export const getStaticProps: GetStaticComponentProps = async (_rendering, layoutData) => {
  if (process.env.JSS_MODE === constants.JSS_MODE.DISCONNECTED) {
    return { studies: [], hasRecruitingStudies: false };
  }

  const routeFields = (layoutData?.sitecore?.route?.fields ?? {}) as SiteMasterRouteFields;
  const context = layoutData?.sitecore?.context;
  const locale = context?.language || 'en';
  const siteName = context?.site?.name || '';

  try {
    const studies = await loadSiteRecruitingStudies(
      routeFields.relatedProtocolSites,
      locale,
      siteName
    );
    return {
      studies,
      hasRecruitingStudies: studies.length > 0,
    };
  } catch {
    return { studies: [], hasRecruitingStudies: false };
  }
};

export default SiteRecruitingStudies;
